import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useDispatch, useSelector } from 'react-redux';
import Form from '@rjsf/core';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';

import ContactActions from '../actions/contact';

import theme from '../constants/theme'; // Required instead of using theme provider from styled components due to it being rendered outside the top level ThemeProvider
import environment from '../constants/environment';

import Button from './Button';
import Loading from './Loading';

const FIELDS_TO_CACHE = [
  'first_name',
  'email',
  'phone',
  'wedding_date',
  'date_flexible',
  'guests',
];

// Hack for https://github.com/rjsf-team/react-jsonschema-form/issues/762
function ObjectFieldTemplate(props) {
  const canExpand = function canExpand() {
    const { formData, schema, uiSchema } = props;
    if (!schema.additionalProperties) {
      return false;
    }
    const { expandable } = getUiOptions(uiSchema);
    if (expandable === false) {
      return expandable;
    }
    // if ui:options.expandable was not explicitly set to false, we can add
    // another property if we have not exceeded maxProperties yet
    if (schema.maxProperties !== undefined) {
      return Object.keys(formData).length < schema.maxProperties;
    }
    return true;
  };

  const { TitleField, DescriptionField } = props;
  return (
    <div id={props.idSchema.$id} className="form-fieldset">
      {(props.uiSchema['ui:title'] || props.title) && (
        <TitleField
          id={`${props.idSchema.$id}__title`}
          title={props.title || props.uiSchema['ui:title']}
          required={props.required}
          formContext={props.formContext}
        />
      )}
      {props.description && (
        <DescriptionField
          id={`${props.idSchema.$id}__description`}
          description={props.description}
          formContext={props.formContext}
        />
      )}
      {props.properties.map(prop => prop.content)}
      {canExpand() && (
        <AddButton
          className="object-property-expand"
          onClick={props.onAddClick(props.schema)}
          disabled={props.disabled || props.readonly}
        />
      )}
    </div>
  );
}

const contactFormCacheKey = 'contact-form-cache';

const Modal = withReactContent(Swal);

const uiSchema = {
  'ui:order': ['first_name', 'last_name', 'Email', '*', 'message'],
  first_name: { 'ui:placeholder': 'Tina' },
  email: { 'ui:placeholder': 'you@website.com' },
  phone: { 'ui:placeholder': '____ _____ _____' },
  // guests: { "ui:placeholder": "Unsure" },
  message: {
    'ui:options': {
      rows: 8,
    },
    'ui:placeholder':
      "Don't forget to include:\n · Wedding location and venue\n · Length of time you require their services\n · Any additional services you require\n · When you are available to have a meet/call with them",
  },
  'ui:options': {},
  'ui:widget': 'textarea',
  'ui:options': {
    rows: 15,
  },
};

const schema = {
  type: 'object',
  required: ['first_name', 'email', 'message'],
  properties: {
    first_name: { type: 'string', title: 'First Name' },
    email: { type: 'string', title: 'Email' },
    // phone: { type: "string", title: "Phone" },
    wedding_date: {
      type: 'string',
      title: 'Wedding Date (or desired wedding date)',
      format: 'date',
    },
    date_flexible: {
      type: 'boolean',
      title: 'This date is flexible',
    },
    // guests: {
    //   type: "string",
    //   title: "Number of Guests",
    //   enum: ["0-50", "51-100", "101-200", "201-300", "301-400", "400+"],
    // },
    message: { type: 'string', title: 'Message', format: 'textarea' },
  },
};

const Wrapper = styled.div`
  width: 100%;

  .control-label {
    .required {
      margin-left: 6px;
      color: #c9c9c9;
    }
  }

  .form-group.field-object {
    width: 100%;
  }

  .form-fieldset, 
  /*aka*/fieldset {
    border: none;
    width: 100%;
    display: ${p => (p.layout === 'stacked' ? 'block' : 'grid')};
    grid-template-columns: 1fr 1fr;

    /* Force stacked view on mobile */
    @media (max-width: 800px) {
      display: block;
    }
  }
  button {
    margin: 0 auto;
  }
  .form-group.field-boolean {
    margin: 0 10px 10px 10px !important;
  }
  .form-group.field {
    align-items: flex-start;
    display: flex;
    flex-basis: ${p => (p.layout === 'stacked' ? '100%' : 'calc(50% - 20px)')};
    flex-direction: column;
    margin: 0 10px;
    font-size: ${theme.fontSize.medium};

    &:last-child {
      grid-column: 2;
      grid-row: 1 / 7;
    }
  }
  .control-label {
    margin-bottom: 8px;
    color: ${theme.body.color};
  }
  .form-control {
    appearance: none;
    background-color: #fff;
    border: solid 1px #c9c9c9;
    border-radius: ${theme.borderRadius};
    color: #222;
    font-family: ${theme.fontFamily.sans};
    font-size: ${theme.fontSize.medium};
    margin-bottom: 16px;
    outline: none;
    padding: 12px 8px;
    transition: box-shadow 0.2s ease-in-out;
    width: 100%;
    max-width: 100%;

    &:focus {
      border-color: #444;
      box-shadow: 0 0 1px 2px ${theme.primary600};
      outline: none;
    }
  }
  select.form-control {
    background-position: calc(100% - 10px) center;
    background-repeat: no-repeat;
    background-size: 8px;
    background-image: url('data:image/svg+xml;charset=US-ASCII,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%22292.4%22%20height%3D%22292.4%22%3E%3Cpath%20fill%3D%22%23333333%22%20d%3D%22M287%2069.4a17.6%2017.6%200%200%200-13-5.4H18.4c-5%200-9.3%201.8-12.9%205.4A17.6%2017.6%200%200%200%200%2082.2c0%205%201.8%209.3%205.4%2012.9l128%20127.9c3.6%203.6%207.8%205.4%2012.8%205.4s9.2-1.8%2012.8-5.4L287%2095c3.5-3.5%205.4-7.8%205.4-12.8%200-5-1.9-9.2-5.5-12.8z%22%2F%3E%3C%2Fsvg%3E');
  }

  textarea.form-control {
    flex: 1;
  }
`;

const log = type => console.log.bind(console, type);

const openErrorModal = () => {
  Modal.fire({
    allowEscapeKey: true,
    allowOutsideClick: true,
    icon: 'error',
    title: 'Problem sending message',
    text:
      'We had a problem sending that message. Please check your connection and try again.',
  });
};

const openSuccessModal = () => {
  Modal.fire({
    allowEscapeKey: true,
    allowOutsideClick: true,
    icon: 'success',
    title: 'Message sent!',
    html: (
      <p>
        You should get a reply soon. While you wait, keep searching for more
        perfect vendors.
      </p>
    ),
    confirmButtonColor: theme.primary600,
    showConfirmButton: false,
  });
};

export default function ContactForm({
  layout = 'stacked',
  vendorId,
  wedding_date,
}) {
  const dispatch = useDispatch();
  const contact = useSelector(state => state.contact);
  const isSending = contact.is_sending;
  const weddingDate = wedding_date
    ? wedding_date.toISOString().substr(0, 10)
    : null;

  const handleSubmit = vendorId => e => {
    const { formData } = e;
    dispatch(ContactActions.cacheContactFields(formData));
    dispatch(ContactActions.setMessageBox(false));
    dispatch(
      ContactActions.sendEnquiry({
        vendorId,
        formData,
        location: 'contact-form',
        onSuccess: () => {
          openSuccessModal();
        },
        onError: () => {
          openErrorModal();
        },
      })
    );
  };

  return (
    <Wrapper layout={layout}>
      {isSending ? (
        <Loading />
      ) : (
        <Form
          schema={schema}
          uiSchema={uiSchema}
          formData={{
            ...contact.contact_details,
          }}
          // onChange={log("changed")}
          onSubmit={e => {
            handleSubmit(vendorId)(e);
          }}
          onError={() => {
            log('errors');
          }}
          ObjectFieldTemplate={ObjectFieldTemplate}
        >
          <Button htmlType="submit">Send Enquiry</Button>
        </Form>
      )}
    </Wrapper>
  );
}
